import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { buildArticleBreadcrumb } from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getArticleById, getArticlesByCategory } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');
  const category = article ? getCategoryBySlug(article.category) : undefined;
  const relatedArticles = article
    ? getArticlesByCategory(article.category).filter((a) => a.id !== article.id).slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <title>文章未找到 - 1024社区</title>
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 pb-16">
            <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                <i className="ri-file-unknow-line text-foreground-400 text-2xl"></i>
              </span>
              <h1 className="text-2xl font-bold font-heading text-foreground-900 mb-3">文章未找到</h1>
              <p className="text-foreground-600 mb-6">抱歉，您要查看的文章不存在或已被删除。</p>
              <Link to="/" className="inline-flex items-center px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap">
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://jxsbzl.cn/article/${article.id}`,
    },
    publisher: {
      '@type': 'Organization',
      name: '1024社区',
      logo: {
        '@type': 'ImageObject',
        url: 'https://jxsbzl.cn/logo.png',
      },
    },
    about: {
      '@type': 'Thing',
      name: article.tags.join(', '),
    },
  };

  return (
    <>
      <ScrollToTop />
      <title>{article.title} - 1024社区</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={article.tags.join(',')} />
      <meta name="author" content={article.author} />
      <link rel="canonical" href={`https://jxsbzl.cn/article/${article.id}`} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:url" content={`https://jxsbzl.cn/article/${article.id}`} />
      <meta property="article:published_time" content={article.publishDate} />
      <meta property="article:author" content={article.author} />
      {article.tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <article className="max-w-3xl mx-auto px-4 md:px-6">
            <Breadcrumb items={buildArticleBreadcrumb(article.id)} />

            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-2.5 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-sm text-foreground-500 whitespace-nowrap">{article.publishDate}</span>
                <span className="text-sm text-foreground-500 whitespace-nowrap">{article.readTime} 分钟阅读</span>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{article.author}</div>
                    <div className="text-xs text-foreground-500 whitespace-nowrap">认证作者</div>
                  </div>
                </div>
                <div className="flex items-center gap-5 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()} 阅读
                  </span>
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <i className="ri-heart-line"></i> {article.likeCount.toLocaleString()} 点赞
                  </span>
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <i className="ri-chat-3-line"></i> {article.commentCount} 评论
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-5">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            <div
              className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-900 prose-p:text-foreground-700 prose-p:leading-relaxed prose-code:bg-secondary-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:before:content-none prose-code:after:content-none prose-a:text-primary-600 prose-strong:text-foreground-900 mb-12"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="border-t border-background-200/70 pt-8">
              <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-4">
                  <button className="flex items-center gap-2 px-4 py-2 rounded-md bg-background-100 hover:bg-primary-50 text-foreground-600 hover:text-primary-600 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-thumb-up-line"></i> 点赞 ({article.likeCount.toLocaleString()})
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 rounded-md bg-background-100 hover:bg-primary-50 text-foreground-600 hover:text-primary-600 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-bookmark-line"></i> 收藏
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 rounded-md bg-background-100 hover:bg-primary-50 text-foreground-600 hover:text-primary-600 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-share-forward-line"></i> 分享
                  </button>
                </div>
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    返回 {category.name} 分类 <i className="ri-arrow-right-line"></i>
                  </Link>
                )}
              </div>

              {relatedArticles.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-4">相关文章</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.id}`}
                        className="group bg-background-100 rounded-lg p-4 hover:bg-primary-50/50 transition-colors"
                      >
                        <h4 className="text-sm font-bold font-heading text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                          {ra.title}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-foreground-500">
                          <span className="whitespace-nowrap">{ra.author}</span>
                          <span>·</span>
                          <span className="whitespace-nowrap">{ra.viewCount.toLocaleString()} 阅读</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}